import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  AppBar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Paper,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CaseIntakeApi, { IntakeReadStarted } from '../../api/CaseIntakeApi';
import { pastedImage } from '../../utils/globalDocumentDrop';
import type { AfterCreate } from '../../utils/paymentOnCreate';
import type { CreatedLink } from '../../utils/paymentLinkSend';
import PaymentLinkStep from '../PaymentLinkStep/PaymentLinkStep';
import type { CreateCaseStart } from './CreateCaseContext';
import CreateCaseReview from './CreateCaseReview';
import { namePastedImage } from './GlobalDocumentDrop';

/** The types the reader takes; the server says why anything else cannot be read. */
const ACCEPT = '.pdf,.png,.jpg,.jpeg,.gif,.webp,.heic,.heif,.html,.htm,.mhtml,.mht';

type Stage =
  | { kind: 'choose'; error: string | null }
  | { kind: 'uploading' }
  | { kind: 'review'; started: IntakeReadStarted; file: File | null; text: string | null }
  | {
      kind: 'payment';
      ticketGuid: string;
      attention?: {
        pendingLink: CreatedLink | null;
        problem: string;
        channels: { email: boolean; sms: boolean } | null;
      };
    };

/**
 * Create case: choose, drop or paste a document → the review screen opens while it is read → Create,
 * which also sends the payment link when that box is ticked → the case. The payment step comes
 * between only when the box was not ticked or the link needs attention (utils/paymentOnCreate).
 *
 * The upload answers in a second or two and the review screen opens at once; its fields fill in when
 * the read finishes (Lloyd, Oct 2026: the whole thing in "under 1-2 minutes, tops"). Nothing is created
 * until Create is pressed, and nothing is sent until Send is.
 */
const CreateCaseDialog: React.FC<{
  start: CreateCaseStart;
  ignoredFiles: number;
  onClose: () => void;
}> = ({ start, ignoredFiles, onClose }) => {
  const navigate = useNavigate();
  const [stage, setStage] = React.useState<Stage>({ kind: 'choose', error: null });
  const [pastedText, setPastedText] = React.useState('');
  const [dragOver, setDragOver] = React.useState(false);
  const begun = React.useRef(false);

  const upload = React.useCallback(async (input: { file: File } | { text: string }) => {
    setStage({ kind: 'uploading' });
    try {
      const started = await CaseIntakeApi.read(input);
      setStage({
        kind: 'review',
        started,
        file: 'file' in input ? input.file : null,
        text: 'text' in input ? input.text : null,
      });
    } catch (error) {
      setStage({
        kind: 'choose',
        error: error instanceof Error ? error.message : 'The document could not be uploaded.',
      });
    }
  }, []);

  React.useEffect(() => {
    if (begun.current) return;
    begun.current = true;
    if (start.file) {
      void upload({ file: start.file });
    } else if (start.existingDocument && start.lockedClient) {
      setStage({ kind: 'uploading' });
      CaseIntakeApi.readDocument(start.lockedClient.userGuid, start.existingDocument.documentGuid)
        .then((started) => setStage({ kind: 'review', started, file: null, text: null }))
        .catch((error) =>
          setStage({
            kind: 'choose',
            error: error instanceof Error ? error.message : 'That document could not be opened.',
          }),
        );
    }
  }, [start, upload]);

  // While choosing, a pasted screenshot is read straight away, wherever the cursor is.
  React.useEffect(() => {
    if (stage.kind !== 'choose') return undefined;
    const onPaste = (event: ClipboardEvent) => {
      const files = Array.from(event.clipboardData?.files ?? []);
      const index = pastedImage(files, {
        focused: document.activeElement as HTMLElement | null,
        dialogOpen: true,
        otherModalOpen: false,
      });
      if (index === null) return;
      event.preventDefault();
      void upload({ file: namePastedImage(files[index]) });
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [stage.kind, upload]);

  const onDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
    const file = event.dataTransfer.files?.[0];
    if (file) void upload({ file });
  };

  /** `notice` is shown at the top of the case (what the one press sent, and where). */
  const openCase = (ticketGuid: string, notice?: string) => {
    onClose();
    navigate(`/ticket?Ticket_guid=${ticketGuid}`, notice ? { state: { caseNotice: notice } } : undefined);
  };

  const afterCreated = (ticketGuid: string, after: AfterCreate) => {
    if (after.kind === 'open-case') {
      openCase(ticketGuid, after.notice);
      return;
    }
    setStage({
      kind: 'payment',
      ticketGuid,
      attention: after.problem
        ? { pendingLink: after.pendingLink, problem: after.problem, channels: after.channels }
        : undefined,
    });
  };

  return (
    <Dialog fullScreen open onClose={onClose} aria-labelledby="create-case-title">
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Typography id="create-case-title" variant="h6" sx={{ flexGrow: 1 }}>
            {stage.kind === 'payment'
              ? 'Send a payment link'
              : start.lockedClient
                ? `Create a case for ${start.lockedClient.name}`
                : 'Create case from a document'}
          </Typography>
          <IconButton
            edge="end"
            onClick={stage.kind === 'payment' ? () => openCase(stage.ticketGuid) : onClose}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/*
        A file dropped anywhere else in the dialog is swallowed: the browser would otherwise open it
        in place of the portal and lose the review in progress.
      */}
      <Box
        sx={{ p: { xs: 2, md: 3 }, overflow: 'auto', flex: 1 }}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => event.preventDefault()}
      >
        {stage.kind === 'choose' && (
          <Stack spacing={2} sx={{ maxWidth: 720, mx: 'auto' }}>
            {stage.error && <Alert severity="error">{stage.error}</Alert>}
            <Paper
              variant="outlined"
              onDragOver={(event) => {
                event.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              sx={{
                p: 4,
                textAlign: 'center',
                borderStyle: 'dashed',
                borderWidth: 2,
                borderColor: dragOver ? 'primary.main' : 'divider',
                bgcolor: dragOver ? 'action.hover' : 'transparent',
              }}
            >
              <UploadFileIcon color="primary" sx={{ fontSize: 48 }} />
              <Typography variant="h6">Drop a citation or an email here</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                A photo or scan of the ticket, an email printed to PDF, a saved web page, or a
                screenshot. You can also paste a screenshot with Ctrl+V (Cmd+V on a Mac).
              </Typography>
              <Button variant="contained" component="label" startIcon={<UploadFileIcon />}>
                Choose file
                <input
                  type="file"
                  hidden
                  accept={ACCEPT}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (file) void upload({ file });
                  }}
                />
              </Button>
            </Paper>
            <Typography variant="subtitle2">Or paste the text of the email or page</Typography>
            <TextField
              multiline
              minRows={5}
              maxRows={14}
              value={pastedText}
              onChange={(event) => setPastedText(event.target.value)}
              placeholder="Paste the email here"
            />
            <Box>
              <Button
                variant="outlined"
                disabled={!pastedText.trim()}
                onClick={() => void upload({ text: pastedText })}
              >
                Read pasted text
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary">
              An email dragged straight out of Outlook (.msg) cannot be read yet. Open it and use Print
              &gt; Save as PDF, or take a screenshot.
            </Typography>
          </Stack>
        )}

        {stage.kind === 'uploading' && (
          <Stack alignItems="center" spacing={2} sx={{ mt: 8 }}>
            <CircularProgress />
            <Typography>Uploading…</Typography>
          </Stack>
        )}

        {stage.kind === 'review' && (
          <>
            {ignoredFiles > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {ignoredFiles + 1} files were dropped. Only the first one is used here.
              </Alert>
            )}
            <CreateCaseReview
              started={stage.started}
              file={stage.file}
              pastedText={stage.text}
              lockedClient={start.lockedClient ?? null}
              onCreated={afterCreated}
            />
          </>
        )}

        {stage.kind === 'payment' && (
          <Box sx={{ maxWidth: 720, mx: 'auto' }}>
            <PaymentLinkStep
              ticketGuid={stage.ticketGuid}
              attention={stage.attention}
              onDone={() => openCase(stage.ticketGuid)}
            />
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default CreateCaseDialog;
